import { Navigate, type RouteObject } from "react-router-dom";
import FolderRoutes from "./folders";
import TemplatesRoutes from "./templates";
import AuthRoutes from "./auth";
import BaseRequirementsRoutes from "./base-requirements";
import VaultRoutes from "./vault";
import UsersRoutes from "./users";
import MyFoldersRoutes from "./my-folders";
import { ProtectedMiddleware } from "../components/middleware/ProtectedMiddleware.tsx";
import { IsValidatedMiddleware } from "../components/middleware/IsValidatedMiddleware.tsx";
import { useAppDispatch, useAppSelector } from "../redux/hooks.ts";
import { onGetUserInfo } from "../redux/actions/authActions.tsx";
import { useEffect } from "react";
import { AdminMiddleware } from "../components/middleware/AdminMiddleware.tsx";
import { NotFoundError } from "../components/errors/NotFoundError.tsx";
import { IsActorIdentifiedMiddleware } from "../components/middleware/IsActorIdentifiedMiddleware.tsx";
import { message } from "antd";
import { useCookieWatch } from "../hooks/useCookieWatch.ts";
import SlaMetricsRoutes from "./sla-metrics";
import SLAUserDashboard from "../components/sla-metrics/SLAUserDashboard.tsx";
import { Layout } from "../layout.tsx";
import { Toaster } from "../components/ui/sonner";

/**
 * RootLayout: top-level route element that renders the Layout (with Outlet)
 * and holds the side-effect logic previously in the AppRouter component.
 */
export const RootLayout = () => {
  const [_, contextHolder] = message.useMessage();
  const dispatch = useAppDispatch();
  const { token, userInfo, loading } = useAppSelector((state) => state.auth);
  useCookieWatch();

  const onGetUserData = async () => {
    if (!token || userInfo !== null || loading) return;

    try {
      await dispatch(onGetUserInfo()).unwrap();
    } catch (error: any) {
      // Error is captured by Sentry via Redux enhancer
    }
  };

  useEffect(() => {
    onGetUserData();
  }, [token]);

  return (
    <>
      {contextHolder}
      <Layout />
      <Toaster richColors position="bottom-right" />
    </>
  );
};

/**
 * Route configuration for createBrowserRouter.
 * All routes are children of the RootLayout which provides the Layout + side-effects.
 */
export const routes: RouteObject[] = [
  {
    element: <RootLayout />,
    children: [
      {
        // The home page used to render the metrics dashboard, which fired the
        // heavy /metrics/summary + /metrics/detailed aggregates on every login.
        // Metrics now live behind /sla-metrics; landing goes straight to work.
        path: "/",
        element: <Navigate to="/folders" replace />,
      },
      {
        path: "/auth/*",
        element: <AuthRoutes />,
      },
      {
        path: "/sla-metrics/",
        element: (
          <AdminMiddleware>
            <IsValidatedMiddleware>
              <SlaMetricsRoutes />
            </IsValidatedMiddleware>
          </AdminMiddleware>
        ),
      },
      {
        path: "/sla-metrics/users/:userId",
        element: (
          <ProtectedMiddleware>
            <IsValidatedMiddleware>
              <SLAUserDashboard />
            </IsValidatedMiddleware>
          </ProtectedMiddleware>
        ),
      },
      {
        path: "/folders/*",
        element: (
          <ProtectedMiddleware>
            <IsValidatedMiddleware>
              <FolderRoutes />
            </IsValidatedMiddleware>
          </ProtectedMiddleware>
        ),
      },
      {
        path: "/templates/*",
        element: (
          <ProtectedMiddleware>
            <IsValidatedMiddleware>
              <TemplatesRoutes />
            </IsValidatedMiddleware>
          </ProtectedMiddleware>
        ),
      },
      {
        path: "/base-requirements/*",
        element: (
          <ProtectedMiddleware>
            <IsValidatedMiddleware>
              <BaseRequirementsRoutes />
            </IsValidatedMiddleware>
          </ProtectedMiddleware>
        ),
      },
      {
        path: "/users/*",
        element: (
          <AdminMiddleware>
            <IsValidatedMiddleware>
              <UsersRoutes />
            </IsValidatedMiddleware>
          </AdminMiddleware>
        ),
      },
      {
        path: "/vault/:id/*",
        element: (
          <IsActorIdentifiedMiddleware>
            <VaultRoutes />
          </IsActorIdentifiedMiddleware>
        ),
      },
      {
        path: "/my-folders/*",
        element: (
          <ProtectedMiddleware>
            <IsValidatedMiddleware>
              <MyFoldersRoutes />
            </IsValidatedMiddleware>
          </ProtectedMiddleware>
        ),
      },
      {
        path: "*",
        element: <NotFoundError />,
      },
    ],
  },
];
